import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

function StatCard({ label, value, sub, accent }) {
  return (
    <div style={{
      background: '#fff', border: '1px solid var(--border)',
      borderRadius: 12, padding: '14px 16px',
      borderTop: `3px solid ${accent}`,
      boxShadow: 'var(--shadow-sm)',
    }}>
      <div style={{ fontSize: 9, fontWeight: 700, color: 'var(--text-label)', textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 700, color: 'var(--text-primary)', lineHeight: 1 }}>{value}</div>
      {sub && <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get('/api/dashboard')
      .then(r => setData(r.data))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div style={{ color: 'var(--text-muted)', fontSize: 13, padding: 20 }}>กำลังโหลด...</div>
  );

  return (
    <div>
      {/* Low stock alerts */}
      {data.low_stock_alerts?.map(a => (
        <div key={a.code} className="alert alert-warn">
          <i className="ti ti-alert-triangle"/>
          <span>{a.name} สต็อกต่ำกว่าขั้นต่ำ — เหลือ {parseFloat(a.stock_qty).toFixed(2)} {a.unit} (ต่ำสุด {parseFloat(a.min_qty).toFixed(2)} {a.unit})</span>
        </div>
      ))}

      {/* Stat cards */}
      <div className="grid-3" style={{ marginBottom: 16 }}>
        {data.raw_material && (
          <StatCard
            label="Raw Material"
            value={data.raw_material.total}
            sub={`รายการ${data.raw_material.low_count > 0 ? ` · ${data.raw_material.low_count} ต่ำกว่า min` : ''}`}
            accent="#2563eb"
          />
        )}
        {data.bulk && (
          <StatCard
            label="Bulk Stock (kg)"
            value={parseFloat(data.bulk.total_kg || 0).toFixed(2)}
            sub={`${data.bulk.scent_count} กลิ่น`}
            accent="#0891b2"
          />
        )}
        {data.finished && (
          <StatCard
            label="Finished (ขวด)"
            value={data.finished.total_bottles || 0}
            sub={`${data.finished.sku_count} SKU`}
            accent="#7c3aed"
          />
        )}
      </div>

      {/* Finished stock table */}
      {data.finished_list?.length > 0 && (
        <>
          <div className="sh">สินค้าสำเร็จรูป (Finished Stock)</div>
          <div className="glass-card">
            <table className="tbl">
              <thead>
                <tr>
                  <th>กลิ่น</th>
                  <th>ขวด / ขนาด</th>
                  <th>คงเหลือ</th>
                  {user?.can_see_cost && <th>ต้นทุน/ขวด</th>}
                  <th>สถานะ</th>
                </tr>
              </thead>
              <tbody>
                {data.finished_list.map((f, i) => (
                  <tr key={i}>
                    <td style={{ fontWeight: 500, color: 'var(--text-primary)' }}>{f.formula_name}</td>
                    <td>{f.bottle_name} · {f.bottle_size_ml} ml</td>
                    <td style={{ fontWeight: 600 }}>{f.qty_bottles} ขวด</td>
                    {user?.can_see_cost && <td>฿{parseFloat(f.cost_per_bottle || 0).toFixed(2)}</td>}
                    <td>
                      <span className={`badge ${f.qty_bottles < 20 ? 'badge-warn' : 'badge-ok'}`}>
                        {f.qty_bottles < 20 ? 'ต่ำ' : 'ปกติ'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Bulk stock table */}
      {data.bulk_list?.length > 0 && (
        <>
          <div className="sh">สต็อก Bulk (ผสมแล้ว รอบรรจุ)</div>
          <div className="glass-card">
            <table className="tbl">
              <thead>
                <tr>
                  <th>กลิ่น</th>
                  <th>Batch</th>
                  <th>kg คงเหลือ</th>
                  {user?.can_see_cost && <th>ต้นทุน/kg</th>}
                  <th>วันที่ผสม</th>
                </tr>
              </thead>
              <tbody>
                {data.bulk_list.map(b => (
                  <tr key={b.id}>
                    <td style={{ fontWeight: 500, color: 'var(--text-primary)' }}>{b.formula_name}</td>
                    <td style={{ fontFamily: 'monospace', fontSize: 11 }}>{b.batch_no}</td>
                    <td style={{ fontWeight: 600 }}>{parseFloat(b.qty_kg).toFixed(4)}</td>
                    {user?.can_see_cost && <td>฿{parseFloat(b.cost_per_kg || 0).toFixed(2)}</td>}
                    <td>{new Date(b.created_at).toLocaleDateString('th-TH')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
